import { forwardRef, type JSX } from 'react';
import type { PolymorphicComponentPropWithRef, PolymorphicRef } from '../types';
import styles from './title.module.css';
import clsx from 'clsx';

export type TitleProps<As extends React.ElementType = 'h2'> =
  PolymorphicComponentPropWithRef<
    As,
    {
      uppercase?: boolean;
      text?: string;
      size?: 1 | 2 | 3 | 4 | 5 | 6 | '1' | '2' | '3' | '4' | '5' | '6' | 'body';
    }
  >;

const TitleComp = <As extends React.ElementType = 'h2'>(
  { as, className, size, text, uppercase, ...rest }: TitleProps<As>,
  ref: PolymorphicRef<As>
) => {
  const Tag = as || 'h2';
  const sizeInt = Number(String(size || as).replace(/^h([1-6])$/i, '$1')) || 1;
  const classes = clsx(
    styles.title,
    styles[size === 'body' ? size : `size${Math.max(1, Math.min(6, sizeInt))}`],
    uppercase && styles.uppercase,
    className
  );

  // Automatically format text breaks if no children and text is provided
  if (text && !rest.children)
    Object.assign(rest, { children: formatTextBreaks(text) });

  return <Tag className={classes} ref={ref} {...rest} />;
};

export type TaglineProps = TitleProps<'span'>;

const Tagline = (
  { className, ...props }: TaglineProps,
  ref: React.ForwardedRef<HTMLSpanElement>
) => {
  return (
    <Title
      as="span"
      size="6"
      uppercase
      className={clsx(styles.tagline, className)}
      ref={ref}
      {...props}
    />
  );
};

const TitleComponent = forwardRef<null>(TitleComp) as <
  As extends React.ElementType = 'h2'
>(
  props: TitleProps<As>
) => JSX.Element;
const TaglineComp = forwardRef<HTMLSpanElement, TaglineProps>(Tagline);

export const Title = Object.assign(TitleComponent, { Tagline: TaglineComp });

(TitleComponent as React.FC).displayName = 'Title';
TaglineComp.displayName = 'Title.Tagline';

const formatTextBreaks = (text: string) =>
  text
    .replace(/--/g, '\u{AD}') // Use unicode instead of &shy; to avoid dangerouslySetInnerHTML
    .replace(/__/g, '\u{A0}') // Use unicode instead of &nbsp; to avoid dangerouslySetInnerHTML
    .replace(/(\d)\s(\d)/g, '$1\u{A0}$2'); // Format number-space-number to automatically avoid breaking up numbers i.e. 100 000
